import { Skeleton, Typography } from "@mui/material";
import moment from "moment";
import ExtractedEditedIco from "../assets/icons/ExtractedEditedIco";
import ExtractedInferredIco from "../assets/icons/ExtractedInferredIco";
import FileExtensionIco from "../assets/icons/FileExtensionIco";
import InferredEditedIco from "../assets/icons/InferredEditedIco";
import InferredExtractedEditedIco from "../assets/icons/InferredExtractedEditedIco";
import JobPencilIco from "../assets/icons/JobPencilIco";
import JobPencilOnlyIco from "../assets/icons/JobPencilOnlyIco";
import JobPenIco from "../assets/icons/JobPenIco";
import JobPenOnlyIco from "../assets/icons/JobPenOnlyIco";
import JobStarIco from "../assets/icons/JobStarIco";
import JobStarOnlyIco from "../assets/icons/JobStarOnlyIco";
import { CustomDiv } from "../modules/chat-bot/style";
import {
  CommonGrid2,
  CustomTooltip,
  DataFound,
} from "../modules/common-components/StyledComponents";

const fontFamily = ["Inter"].join(",");

const colors = {
  blue: {
    main: "#004E87",
    dark: "#005689",
    light: "#0065A1",
    100: "#F0F9FF",
    200: "#043F69",
    300: "#0B6AAF",
    400: "#165BAA",
    500: "#007AC3",
    600: "#F5F8FB",
    700: "#DDF1FF",
    750: "#D5DDF3",
    800: "#285E85",
    850: "#DEEEF7",
    900: "#E5EAEE",
    950: "#003A64",
    1000: "#F6F8FB",
  },
  green: {
    light: "#DDF6DB",
    main: "#76B729",
    dark: "#789F48",
    100: "#44740B",
    200: "#83B943",
    300: "#00A28E",
    400: "#1C1B1F",
    500: "#3C9A56",
    600: "#DDECD0",
    700: "#61A620",
    800: "#EAF5E7",
    900: "#008000",
    950: "#80c52b",
  },
  grey: {
    A100: "#6A6A6D",
    A200: "#A6A6A6",
    A400: "#F9F6F2",
    A700: "#E4E4E4",
    50: "#616167",
    100: "#6C6C6C",
    200: "#F6F6F6",
    250: "#D8D8D8",
    300: "#B9C1CC",
    350: "#DDDDDD",
    400: "#969696",
    450: "#0000001f",
    500: "#E6E6E6",
    600: "#D1D1D1",
    650: "#DFF2FF",
    700: "#BDBDBD",
    750: "#E0E0E0",
    800: "#E5E6EC",
    850: "#C1C1C1",
    900: "#B0B0B0",
    950: "#D9D9D9",
  },
  black: {
    main: "#1E1F21",
    light: "#49454F",
    dark: "#231C16",
    100: "#000000",
    200: "0A0A0A",
  },
  sea: {
    main: "#4A9F9A",
    dark: "#FBFBFB",
    light: "#F5FAFF",
    100: "#EFF4F9",
    200: "#DBE5ED",
    300: "#E5EDF1",
    400: "#5B35D5",
    500: "#ECE5F6",
    600: "#658EA7",
    700: "#1567a3",
  },
  red: {
    main: "#E05F62",
    dark: "#CF5A5A",
    light: "#FADCDC",
    100: "#A6361F",
    200: "#DB5A63",
    300: "#E55E5E",
    400: "#BB2F38",
    500: "#F9DFE0",
    600: "#FF0000",
  },
  sandal: {
    main: "#E05F62",
  },
  warning: {
    main: "#FA8C42",
    dark: "#F18C33",
    light: "#FFF1DE",
  },
  white: {
    main: "#fff",
  },
  yellow: {
    dark: "#FFAF49",
    light: "#CEA046",
  },
};

export { colors, fontFamily };

export const hexToRgba = (hex, alpha) => {
  let r = 0,
    g = 0,
    b = 0;

  // Check for 3-digit hex
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  }
  // Check for 6-digit hex
  else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getColor = (score) => {
  let color;
  const roundedScore = Math.round(score);

  if (roundedScore <= 35) {
    color = "#7F56D9";
  } else if (roundedScore > 35 && roundedScore <= 70) {
    color = "#F79009";
  } else if (roundedScore > 70 && roundedScore <= 100) {
    color = "#17B26A";
  }

  return color;
};

export const HandleSkeletion = ({
  height,
  width,
  marginRight,
  background,
  transform,
  marginBottom,
  borderRadius,
  margin,
}) => {
  return (
    <Skeleton
      height={height}
      width={width}
      animation="wave"
      sx={{
        margin: margin,
        marginRight: marginRight,
        marginBottom: marginBottom,
        transform: transform,
        borderRadius: borderRadius,
        // background: background || "rgb(131 0 255 / 10%)",
        background: background || "rgb(0 24 105 / 11%)",
      }}
    />
  );
};

export const ErrorFound = ({ title }) => {
  return (
    <DataFound padding="10px">
      <CustomDiv fontSize="20px">👋</CustomDiv>

      {title}
    </DataFound>
  );
};

export const GetFileIcon = ({ file, width }) => {
  const { name, type } = file;
  let extension = "";

  if (type?.includes("pdf") || type?.includes("PDF")) {
    extension = "PDF";
    return (
      <FileExtensionIco
        extension={extension}
        extensionColor={"#D92D20"}
        width={width}
      />
    );
  } else if (
    type?.includes("wordprocessingml.document") ||
    name?.endsWith(".docx") ||
    type.includes(".docx") ||
    type.includes("DOCX")
  ) {
    extension = "DOCX";
    return (
      <FileExtensionIco
        extension={extension}
        extensionColor={"#155EEF"}
        width={width}
      />
    );
  } else if (
    type?.includes("msword") ||
    name?.endsWith(".doc") ||
    type.includes(".doc") ||
    type.includes("DOC")
  ) {
    extension = "DOC";
    return (
      <FileExtensionIco
        extension={extension}
        extensionColor={"#155EEF"}
        width={width}
      />
    );
  } else if (
    type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    name?.endsWith(".xlsx")
  ) {
    extension = "XLSX";
    return (
      <FileExtensionIco
        extension={extension}
        extensionColor={"#22C55E"}
        width={width}
      />
    );
  } else if (type === "application/vnd.ms-excel" || name?.endsWith(".xls")) {
    extension = "XLS";
    return (
      <FileExtensionIco
        extension={extension}
        extensionColor={"#16A34A"}
        width={width}
      />
    );
  }

  return <FileExtensionIco extension={""} extensionColor={""} width={width} />;
};

export const buildUrl = (base, additionalParams = {}, getBreadcrumb) => {
  const currentQuery = new URLSearchParams(window.location.search);
  const params = new URLSearchParams();

  const keys = ["id", "title", "value", "match-id", "document"];

  keys.forEach((key) => {
    const value = additionalParams[key] ?? currentQuery.get(key);
    if (value && value !== "null" && value !== "undefined") {
      params.append(key, value);
    }
  });

  if (
    getBreadcrumb &&
    getBreadcrumb !== "null" &&
    getBreadcrumb !== "undefined"
  ) {
    params.append("breadcrumbs", getBreadcrumb);
  }

  return `${base}?${params.toString()}`;
};

export const checkEndDate = (dayNumber) => {
  const year = new Date().getFullYear();

  const date = moment()
    .year(year)
    .startOf("year")
    .add(dayNumber - 1, "days");
  return date.format("MMM D YYYY");
};

// Color Icons for job
export const getIcons = (data) => {
  if (!data) return [];
  const flags = {
    extracted: false,
    inferred: false,
    user_added: false,
  };

  for (const { name, content } of data) {
    if (!content?.length) continue;
    if (flags.hasOwnProperty(name)) flags[name] = true;
  }

  return [
    flags.extracted && getToolTipIcon("Extracted"),
    flags.inferred && getToolTipIcon("Inferred"),
    flags.user_added && getToolTipIcon("User Added"),
  ].filter(Boolean);
};

export const getToolTipIcon = (name, iconOnly = false) => {
  const iconMap = {
    Extracted: {
      icon: <JobPencilIco />,
      textColor: "#344054",
      backgroundColor: "#EAECEE",
      toolTipIcon: <JobPencilOnlyIco />,
      multiSelectIcon: <JobPencilOnlyIco />,
    },
    Inferred: {
      icon: <JobStarIco />,
      textColor: "#FFFFFF",
      backgroundColor: "#0070F2",
      toolTipIcon: <JobStarOnlyIco fill="#FFFFFF" />,
      multiSelectIcon: <JobStarOnlyIco />,
    },
    "User Added": {
      icon: <JobPenIco />,
      textColor: "#FFFFFF",
      backgroundColor: "#049F9A",
      toolTipIcon: <JobPenOnlyIco fill="#FFFFFF" />,
      multiSelectIcon: <JobPenOnlyIco />,
    },
  };
  return (
    <CustomTooltip
      title={getToolTipTitle(
        name,
        iconMap[name].toolTipIcon,
        iconMap[name].textColor
      )}
      placement="top"
      backgroundColor={iconMap[name].backgroundColor}
      padding="5px 10px"
      borderRadius="50px"
    >
      <CustomDiv
        display={"flex"}
        customStyle={{ alignItems: "center" }}
        padding="0"
      >
        {iconOnly ? iconMap[name].multiSelectIcon : iconMap[name].icon}
      </CustomDiv>
    </CustomTooltip>
  );
};
export const getToolTipTitle = (name, icon, textColor = "#344054") => {
  const nameMap = {
    Extracted: "Parsed",
    Inferred: "Inferred",
    "User Added": "Edited",
  };
  return (
    <CommonGrid2
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
      textAlign={"center"}
      gap={"5px"}
    >
      {icon}
      <Typography
        variant="p"
        fontSize="12px"
        fontWeight={600}
        color={textColor}
      >
        {nameMap[name]}
      </Typography>
    </CommonGrid2>
  );
};

export const buildFinalData = (source = [], data = []) => {
  const valueSourceMap = {};

  source.forEach(({ name, content }) => {
    Array.isArray(content) &&
      content.forEach((value) => {
        valueSourceMap[value] = name;
      });
  });

  return data
    .filter((item) => valueSourceMap[item.name])
    .map((item) => ({
      ...item,
      color: valueSourceMap[item.name],
    }));
};

export const addColorToData = (source = [], data = []) => {
  const valueSourceMap = {};

  source.forEach(({ name, content = [] }) => {
    Array.isArray(content) &&
      content.forEach((value) => {
        valueSourceMap[value] = name;
      });
  });

  return data.map((item) => ({
    ...item,
    color: valueSourceMap[item.name] || "",
  }));
};

export const rebuildSourceFromFinalData = (finalData) => {
  const sourceMap = {
    extracted: [],
    inferred: [],
    user_added: [],
  };

  finalData.forEach(({ name, color }) => {
    if (sourceMap[color]) {
      sourceMap[color].push(name);
    }
  });

  return [
    { name: "extracted", content: sourceMap.extracted },
    { name: "inferred", content: sourceMap.inferred },
    { name: "user_added", content: sourceMap.user_added },
  ];
};
export const getIconForKeyColors = (
  keyColors,
  skillName,
  familiarity,
  experience_in_yrs,
  about_the_skill
) => {
  const ICON_MAP = {
    extracted: <JobPencilIco />,
    inferred: <JobStarIco />,
    user_added: <JobPenIco />,

    "extracted+inferred": <ExtractedInferredIco />,
    "extracted+user_added": <ExtractedEditedIco />,
    "inferred+user_added": <InferredEditedIco />,

    three: <InferredExtractedEditedIco />,
  };
  if (keyColors === undefined || keyColors === null) return null;
  const uniqueValues = [...new Set(Object.values(keyColors))];
  var returnIcon = null;

  if (uniqueValues.length === 3) {
    returnIcon = ICON_MAP.three;
  }

  if (uniqueValues.length === 1) {
    returnIcon = ICON_MAP[uniqueValues[0]];
  }

  // normalize order so combinations are consistent
  const comboKey = uniqueValues.sort().join("+");
  return (
    <CustomTooltip
      title={getToolTipTitleForKeyColors(
        keyColors,
        skillName,
        familiarity,
        experience_in_yrs,
        about_the_skill
      )}
      placement="top"
      padding="10px 10px"
      borderRadius="10px"
      backgroundColor="#fff"
      // open={true}
      boxShadow="0px 0px 10px 0px rgba(0, 0, 0, 0.1)"
    >
      <CustomDiv
        cursor="pointer"
        display={"flex"}
        customStyle={{
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          boxShadow: "none",
          padding: "0",
        }}
      >
        {returnIcon || ICON_MAP[comboKey]}
      </CustomDiv>
    </CustomTooltip>
  );
};
export const getToolTipTitleForKeyColors = (
  keyColors,
  skillName,
  familiarity,
  experience_in_yrs,
  about_the_skill
) => {
  const valueMap = {
    skillName: { value: skillName, key: "type", name: "" },
    familiarity: {
      value: familiarity,
      key: "expertise_level",
      name: "Familiarity",
    },
    experience_in_yrs: {
      value: experience_in_yrs,
      key: "experience_in_years",
      name: "Experience",
    },
  };
  const iconMap = {
    extracted: {
      icon: <JobPencilOnlyIco size="12px" />,
      textColor: "#223548",
      backgroundColor: "#EAECEE",
      text: "Parsed",
    },
    inferred: {
      icon: <JobStarOnlyIco size="10px" />,
      textColor: "#223548",
      backgroundColor: "#D1EFFF",
      text: "Inferred",
    },
    user_added: {
      icon: <JobPenOnlyIco size="21px" />,
      textColor: "#223548",
      backgroundColor: "#DAFDF5",
      text: "Edited",
    },
  };
  const values = ["familiarity", "experience_in_yrs"];
  return (
    <CommonGrid2
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
      textAlign={"center"}
      gap={"5px"}
      flexDirection={"column"}
    >
      <CommonGrid2
        display={"flex"}
        alignItems={"center"}
        justifyContent={"start"}
        flexDirection={"column"}
        gap={"5px"}
        color="#000"
        width={"100%"}
        textAlign={"start"}
      >
        <CommonGrid2
          display={"flex"}
          gap={"5px"}
          alignItems={"center"}
          justifyContent={"start"}
          textAlign={"start"}
          width={"100%"}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {valueMap["skillName"].value}
          </Typography>
          <CommonGrid2
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={"5px"}
            padding={"5px 5px"}
            borderRadius={"5px"}
            backgroundColor={
              iconMap?.[keyColors?.[valueMap?.["skillName"]?.key]]
                ?.backgroundColor
            }
            color={
              iconMap?.[keyColors?.[valueMap?.["skillName"]?.key]]?.textColor
            }
            height={"20px"}
            minWidth={"auto"}
          >
            {iconMap?.[keyColors?.[valueMap?.["skillName"]?.key]]?.icon}
            <Typography
              variant="subtitle2xs"
              fontWeight={700}
              color={
                iconMap?.[keyColors?.[valueMap?.["skillName"]?.key]]?.textColor
              }
            >
              {iconMap?.[keyColors?.[valueMap?.["skillName"]?.key]]?.text}
            </Typography>
          </CommonGrid2>
        </CommonGrid2>
        {about_the_skill && (
          <Typography variant="subtitle2" color="#101828" fontWeight={500}>
            {about_the_skill}
          </Typography>
        )}
      </CommonGrid2>

      <CommonGrid2
        display={"flex"}
        alignItems={"center"}
        justifyContent={"start"}
        flexDirection={"row"}
        gap={"10px"}
        color="#000"
        textAlign={"start"}
        width={"100%"}
      >
        {values.map((item) => (
          <CommonGrid2
            display={"flex"}
            flexDirection={"column"}
            alignItems={"start"}
            justifyContent={"start"}
            textAlign={"start"}
            width={"100%"}
            // gap={"1px"}
          >
            <Typography variant="subtitle2" fontWeight={600} color="#5B738B">
              {valueMap?.[item]?.name}
            </Typography>
            <Typography variant="subtitle2" fontWeight={600}>
              {valueMap?.[item]?.value}
              {valueMap?.[item]?.key === "experience_in_years" && " years"}
            </Typography>
            <CommonGrid2
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={"5px"}
              margin={"2px 0px 0px"}
              padding={"5px 5px"}
              borderRadius={"5px"}
              backgroundColor={
                iconMap?.[keyColors?.[valueMap?.[item]?.key]]?.backgroundColor
              }
              width={"auto"}
              height={"20px"}
            >
              {iconMap?.[keyColors?.[valueMap?.[item]?.key]]?.icon}
              <Typography
                variant="subtitle2xs"
                fontWeight={700}
                color={iconMap?.[keyColors?.[valueMap?.[item]?.key]]?.textColor}
              >
                {iconMap?.[keyColors?.[valueMap?.[item]?.key]]?.text}
              </Typography>
            </CommonGrid2>
          </CommonGrid2>
        ))}
      </CommonGrid2>
    </CommonGrid2>
  );
};
//Demo Version
export const domainOptions = [
  "E-commerce",
  "Agriculture",
  "Automotive",
  "Banking & Finance",
  "Biotechnology",
  "Construction",
  "Consumer Goods",
  "Education",
  "Energy & Utilities",
  "Engineering",
  "Entertainment",
  "Environmental Services",
  "Fashion",
  "Healthcare",
  "Hospitality & Tourism",
  "Human Resources",
  "Information Technology",
  "Insurance",
  "Legal Services",
  "Logistics & Supply Chain",
  "Manufacturing",
  "Marketing & Advertising",
  "Media & Broadcasting",
  "Nonprofit",
  "Pharmaceuticals",
  "Real Estate",
  "Retail",
  "Science & Research",
  "Telecommunications",
  "Transportation",
  "Waste Management",
];

export const clientTitleOptions = [
  "CDIO",
  "CHRO",
  "CEO",
  "VP Technology",
  "CISO",
  "CTO",
  "COO",
  "CEO",
  "Chief Design Officer",
  "SVP - Technology",
];

export const highlightBoolean = (text = "") => {
  if (typeof text !== "string") return "";

  return text
    .replace(/\bAND\b/g, '<span class="boolean-and">AND</span>')
    .replace(/\bOR\b/g, '<span class="boolean-or">OR</span>');
};
